import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';

import {
  useGetEmployeesByTeamQuery,
  useGetEmployeeByNameQuery,
} from '@features/employee/hooks/index';
import { useGetEmployeesByCompanyQuery } from '@features/employee/hooks/index';
import useGetTeamQuery from '@features/team/hooks';
import Alert from '@shared/components/atoms/Alert';

import OrganizationTree from '@features/document-write/components/OrganizationTree/OrganizationTree';

import styles from '@pages/admin/organization/components/EmployeeModal/EmployeeModal.module.scss';

/**
 * EmployeeModal 컴포넌트
 * - 대표자 선택 모달
 * - ApprovalLineModal과 동일한 UI/UX로 리팩토링
 * - 역할 선택 기능 없음 (번호 | 이름 | 부서 표시)
 */
function EmployeeModal({ isOpen, onClose, onSelect, title, selectedRecipients = [] }) {
  const [isCompanySelected, setIsCompanySelected] = useState(false);
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  // const [selectedEmployees, setSelectedEmployees] = useState([]);
  // const [checkedEmployees, setCheckedEmployees] = useState(new Set());

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  const MAX_SELECTED_EMPLOYEES = 20; // 참조자/시행자는 넉넉하게 20명으로 설정 (필요시 조정)

  // 모달 오픈 시 배경(body) 스크롤 방지
  useEffect(
    function () {
      if (!isOpen) {
        return undefined;
      }

      const currentCount = Number(document.body.dataset.modalLockCount || '0');
      const nextCount = currentCount + 1;
      document.body.dataset.modalLockCount = String(nextCount);

      if (currentCount === 0) {
        document.body.dataset.modalLockOverflow = document.body.style.overflow || '';
        document.body.dataset.modalLockPaddingRight = document.body.style.paddingRight || '';

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          document.body.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      return function () {
        const count = Number(document.body.dataset.modalLockCount || '0');
        const restoredCount = Math.max(0, count - 1);
        document.body.dataset.modalLockCount = String(restoredCount);

        if (restoredCount === 0) {
          document.body.style.overflow = document.body.dataset.modalLockOverflow || '';
          document.body.style.paddingRight = document.body.dataset.modalLockPaddingRight || '';
          delete document.body.dataset.modalLockOverflow;
          delete document.body.dataset.modalLockPaddingRight;
        }
      };
    },
    [isOpen],
  );

  // Alert 표시 함수
  function showAlert(message) {
    setAlertState({
      isOpen: true,
      message,
    });
  }

  // Alert 닫기 함수
  function closeAlert() {
    setAlertState({
      isOpen: false,
      message: '',
    });
  }

  // 회사 코드 가져오기 (Zustand store에서)
  const companyCode = useAuthStore((state) => state.companyCode);

  function handleCompanySelect() {
    setSelectedTeamCode(null);
    setIsCompanySelected(true);
    setSearchName('');
    setSearchTrigger('');
  }
  const companyEmployeeQuery = useGetEmployeesByCompanyQuery(companyCode, {
    enabled: isOpen && isCompanySelected && !!companyCode,
  });

  const companyEmployeesRaw = companyEmployeeQuery.data?.resultData?.employeeList || [];

  const companyEmployees = companyEmployeesRaw.filter(function (emp) {
    return emp.position === '대표이사' || emp.position === '대표';
  });

  // 팀 목록 조회
  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  // 팀별 사원 목록 조회
  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode,
  });
  const teamEmployees = employeeQuery.data?.resultData?.employeeList || [];

  const employees = isCompanySelected ? companyEmployees : teamEmployees;
  // 사원 이름 검색
  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];

  // 모달이 열릴 때마다 selectedRecipients를 기반으로 selectedEmployees 초기화
  useEffect(() => {
    if (isOpen) {
      if (selectedRecipients && selectedRecipients.length > 0) {
        const r = selectedRecipients[0];

        setSelectedEmployee({
          enumber: Number(r.enumber) || 0,
          name: r.name || '',
          position: r.position || '',
          team: r.team || '',
        });
      } else {
        setSelectedEmployee(null);
      }
    } else {
      setSelectedTeamCode(null);
      setSearchName('');
      setSearchTrigger('');
      setSelectedEmployee(null);
    }
  }, [isOpen]);

  // 팀 선택 핸들러
  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setSearchName('');
    setSearchTrigger('');
  }

  // 중복 직원 확인
  // function checkDuplicateEmployee(employee) {
  //   return selectedEmployees.some((emp) => emp.enumber === employee.enumber);
  // }

  // 체크박스 토글
  function handleCheckboxToggle(employee) {
    const enumber = Number(employee.enumber);

    setSelectedEmployee((prev) => {
      // 같은 사람을 다시 클릭하면 선택 해제
      if (prev && prev.enumber === enumber) {
        return null;
      }

      // 무조건 이 사람으로 교체
      return {
        enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
      };
    });
  }

  // 검색 핸들러
  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 저장 핸들러
  function handleSave() {
    if (onSelect && selectedEmployee) {
      onSelect([selectedEmployee]); // 상위 구조 유지 위해 배열로 전달
    }
    onClose();
  }

  // 검색 결과 표시 여부
  const showSearchResults = searchTrigger.length >= 1;
  const displayEmployees = showSearchResults ? searchResults : employees;

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title || '수신자 선택'}</h2>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentLayout}>
            {/* 왼쪽: 조직도 트리 */}
            <div className={`${styles.columnContainer} ${styles.treeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>조직도</div>
              </div>
              <div className={styles.treeContent}>
                <OrganizationTree
                  companyName={teamList?.[0]?.company || '링크아이'}
                  teamList={teamList}
                  selectedTeamCode={selectedTeamCode}
                  onSelectTeam={handleTeamSelect}
                  onSelectCompany={handleCompanySelect}
                  isCompanySelected={isCompanySelected}
                  height={510}
                />
              </div>
            </div>

            {/* 오른쪽: 임직원 목록 */}
            <div className={`${styles.columnContainer} ${styles.employeeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>임직원</div>
                <div className={styles.searchBlock}>
                  <div className={styles.searchRow}>
                    <div className={styles.searchLabelCell}>이름</div>
                    <div className={styles.searchControlCell}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        style={{ width: '130px' }}
                        value={searchName}
                        onChange={function (e) {
                          setSearchName(e.target.value);
                        }}
                        onKeyDown={function (e) {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                      />
                      <button type="button" className={styles.smallButton} onClick={handleSearch}>
                        검색
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 직원 목록 테이블 */}
              <div className={styles.employeeTableBlock}>
                <div className={styles.employeeTableSection}>
                  <table className={styles.employeeTable}>
                    <thead>
                      <tr>
                        <th className={styles.thCenter}>부서</th>
                        <th className={styles.thCenter}>직급</th>
                        <th className={styles.thCenter}>이름</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEmployees.length > 0 ? (
                        displayEmployees.map(function (employee) {
                          const empNumber = Number(employee.enumber);
                          const isSelected =
                            selectedEmployee && selectedEmployee.enumber === empNumber;

                          return (
                            <tr
                              key={`employee-${empNumber}`}
                              className={`${styles.employeeRow} ${isSelected ? styles.selectedRow : ''}`}
                              onClick={function () {
                                handleCheckboxToggle(employee);
                              }}
                            >
                              <td className={styles.tdCenter}>{employee.team || '-'}</td>
                              <td className={styles.tdCenter}>{employee.position || '-'}</td>
                              <td className={styles.tdCenter}>{employee.name}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {showSearchResults
                              ? '검색 결과가 없습니다.'
                              : '부서를 선택하거나 검색하세요.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnCancel}`}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnSave}`}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>

      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>,
    document.body,
  );
}

export default EmployeeModal;
